import { zodResolver } from '@hookform/resolvers/zod';
import { Box, FormControl, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BetterFetchOption } from 'better-auth/react';
import { useEffect, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { Controller, useForm } from 'react-hook-form';

import { createZodPostVerifyOtpRequest } from '@Common/types/bo_external/auth/request_email';

import { TypographyTranslated } from '@sh4res/bo-common/components';
import { CustomLocalesMenuButton } from '@sh4res/bo-common/components/CustomLocalesMenuButton';
import { PageTitle } from '@sh4res/bo-common/components/PageTitle';
import { isProdEnv } from '@sh4res/bo-common/utils/env';

import { AxaTypography } from '@Components/AxaTypography';
import { OTPInput } from '@Components/InputOTP';
import { LoginPageCTA } from '@Components/LoginPageCTA';
import { TranslationKeys } from '@I18n/types';
import { logAction, reportError } from '@Utils/analytics';
import { authClient } from '@Utils/authClient';

const VerifyOtpPageFormWrapper = styled('form')`
  text-align: center;
  display: flex;
  flex-direction: column;
  min-width: 290px;
  max-width: min-content;
`;

interface VerifyOtpPageFormProps {
  token?: string;
  autoSend?: boolean;
  showLanguagePicker: boolean;
  type: 'email' | 'sms' | 'support';
  alternateEmail?: string;
  send: () => Promise<{ error: { message?: string } | null }>;
  verify: (
    code: string,
    fetchOptions: BetterFetchOption,
  ) => Promise<{ error: { message?: string } | null }>;
  onSuccess: (phoneNumberVerified: boolean) => void;
  onExpired: (state: null) => void;
}

const typeToSubheaderTranslationMapping: Record<
  Exclude<VerifyOtpPageFormProps['type'], 'email'>,
  TranslationKeys
> = {
  support: 'login_page.messages.verify_otp.subheaderSupport',
  sms: 'login_page.messages.verify_otp.subheaderSMS',
};

function getVerifyOtpSubheaderLabel(
  type: VerifyOtpPageFormProps['type'],
  alternateEmail?: string,
): TranslationKeys {
  if (type === 'email' && alternateEmail) {
    return 'login_page.messages.verify_otp.subheaderAlternateEmail';
  }
  if (type === 'email') {
    return 'login_page.messages.verify_otp.subheaderEmail';
  }
  return typeToSubheaderTranslationMapping[type];
}

export function VerifyOtpPageForm({
  token,
  autoSend = false,
  type,
  alternateEmail,
  send,
  verify,
  onSuccess,
  onExpired,
  showLanguagePicker,
}: VerifyOtpPageFormProps) {
  const codeSent = useRef(false);
  const notify = useNotify();
  const translate = useTranslate();
  const testId = 'BoExternalVerifyOtpPageForm';

  const codeType = type === 'support' ? 'backup_code' : 'otp';

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: 'onBlur',
    resolver: zodResolver(createZodPostVerifyOtpRequest(codeType)),
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      if (codeSent.current) {
        return;
      }
      codeSent.current = true;

      if (autoSend) {
        const { error } = await send();
        if (error) {
          // oxlint-disable-next-line no-console
          // eslint-disable-next-line no-console
          console.error(error);
          reportError('OTP send failed', error);
          onExpired(null);
          notify('login_page.messages.verify_otp.expired', { type: 'error' });
          return;
        }
      }

      // In DEV & Staging we should get the fake OTP
      if (!isProdEnv()) {
        const { data } = await authClient.qa.otpCode({ query: { token } });
        if (!data || !data.code) {
          return;
        }
        notify('login_page.messages.verify_otp.testCode', {
          type: 'info',
          autoHideDuration: 300_000, // 5 minutes
          messageArgs: { code: data.code },
        });
      }
    })();
  }, [token, autoSend, notify, onExpired, send]);

  const onSubmit = async (formData: unknown) => {
    setLoading(true);
    const { code } = formData as { code: string };
    await verify(code, {
      onResponse() {
        setLoading(false);
      },
      onSuccess({ data }) {
        logAction('OTP verified');
        onSuccess(data.user?.phoneNumberVerified);
      },
      onError({ error }) {
        // oxlint-disable-next-line no-console
        // eslint-disable-next-line no-console
        console.error(error);
        reportError('OTP verification failed', error);
        reset();
        notify('login_page.messages.verify_otp.failed', { type: 'error' });
      },
    });
  };

  const rawHeaderText = 'login_page.messages.verify_otp.header';
  const subheaderLabel = getVerifyOtpSubheaderLabel(type, alternateEmail);

  return (
    <>
      <PageTitle title={translate(rawHeaderText)} />
      <VerifyOtpPageFormWrapper noValidate onSubmit={handleSubmit(onSubmit)}>
        {showLanguagePicker && (
          <Box position="absolute" top={24} right={24}>
            <CustomLocalesMenuButton testId={`${testId}_LocalesMenuButton`} />
          </Box>
        )}
        <TypographyTranslated
          label={rawHeaderText}
          component={AxaTypography}
          variant="h5"
          sx={{ mt: '40px', px: '24px', width: 'max-content' }}
        />
        <TypographyTranslated
          label={subheaderLabel}
          variant="body1"
          sx={{ my: '16px', textAlign: 'justify' }}
          variables={alternateEmail ? { alternateEmail } : undefined}
        />

        <FormControl
          variant="outlined"
          sx={{
            borderRadius: '16px',
            mb: '24px',
            width: '100%',
          }}>
          <Controller
            name="code"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <OTPInput
                data-testid={`${testId}_CodeInput`}
                {...field}
                type={codeType}
                onComplete={handleSubmit(onSubmit)}
              />
            )}
          />
        </FormControl>

        <Stack alignItems="end">
          <LoginPageCTA
            data-testid={`${testId}_SubmitButton`}
            type="submit"
            variant="contained"
            disabled={!isValid || loading}
            sx={{ width: 'max-content' }}>
            <TypographyTranslated
              component={AxaTypography}
              label="login_page.messages.form.cta_text"
            />
          </LoginPageCTA>
        </Stack>
      </VerifyOtpPageFormWrapper>
    </>
  );
}
